import { Analysis, DashboardConfigurationDependencies, Granularity } from '../../../../dashboard-config/dashboard-config.model';
import { DashboardFilter } from '../../../../filters/dashboard_filter';
import { LocalRowColumnSingleFilterValueFilterApplicator } from '../../../../filters/dashboard_filter_applicators';
import {
  DashboardDistrictsMultiSelectFilter,
  DashboardRemoteSubMarketSingleSelectFilter,
  DashboardStateSingleSelectFilter,
  DashboardStatesMultiSelectFilter,
  DashboardSubMarketSingleSelectFilter,
  DashboardWzClassSingleSelectFilter,
} from '../../../../filters/dashboard_filter_implementations';

export const INDEX_RANKING_SHELL_ID = 'index-ranking' as const;

export type DashboardIndexRankingShell = 'states' | 'wzClassStates' | 'districts';

export interface DashboardIndexRankingShellProfile {
  id: typeof INDEX_RANKING_SHELL_ID;
  granularityId: Granularity;
  analysisId: Analysis;
  /** Chart heading names only the groups, not their scope (legacy wording; see the remodel tracker). */
  headingWithoutScope?: boolean;
  chartSyncable: boolean;
  chartHasSlider: boolean;
  /** Overrides the map's feature label column (the Kreise map shows each district's rank). */
  mapValueLabelKey?: 'rank';
  hideMapValueDisplay: boolean;
  buildGranularityFilters: (
    deps: DashboardConfigurationDependencies,
    /** `defaultState`: state preselected at Kreise (`ProductRules` district selections). */
    options?: { defaultState?: string },
  ) => DashboardFilter<any>[];
}

export const dashboardIndexRankingShellProfiles: Record<DashboardIndexRankingShell, DashboardIndexRankingShellProfile> = {
  states: {
    id: INDEX_RANKING_SHELL_ID,
    granularityId: Granularity.States,
    analysisId: Analysis.Index,
    chartSyncable: true,
    chartHasSlider: false,
    hideMapValueDisplay: false,
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: false,
      }),
      new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {}),
    ],
  },
  wzClassStates: {
    id: INDEX_RANKING_SHELL_ID,
    granularityId: Granularity.WzClassStates,
    analysisId: Analysis.Index,
    chartSyncable: true,
    chartHasSlider: false,
    hideMapValueDisplay: false,
    buildGranularityFilters: (deps) => [
      new DashboardStatesMultiSelectFilter(deps.localDatasetsService, {
        addSumGermany: false,
      }),
      new DashboardRemoteSubMarketSingleSelectFilter(deps.localDatasetsService, {}),
      new DashboardWzClassSingleSelectFilter(deps.localDatasetsService, {}),
    ],
  },
  districts: {
    id: INDEX_RANKING_SHELL_ID,
    granularityId: Granularity.Districts,
    analysisId: Analysis.Index,
    headingWithoutScope: true,
    chartSyncable: true,
    chartHasSlider: true,
    mapValueLabelKey: 'rank',
    hideMapValueDisplay: true,
    buildGranularityFilters: (deps, options) => {
      const stateFilter = options?.defaultState
        ? new DashboardStateSingleSelectFilter(deps.localDatasetsService, {
            name: 'state',
            addSumGermany: true,
            defaultValue: options.defaultState,
            ignoreProductSetting: true,
            applicator: new LocalRowColumnSingleFilterValueFilterApplicator(
              'state_id',
              (_, filterValue) => filterValue.toString() === '-1',
            ),
          })
        : new DashboardStateSingleSelectFilter(deps.localDatasetsService, {
            name: 'state',
            addSumGermany: true,
            applicator: new LocalRowColumnSingleFilterValueFilterApplicator(
              'state_id',
              (_, filterValue) => filterValue.toString() === '-1',
            ),
          });

      return [
        stateFilter,
        new DashboardDistrictsMultiSelectFilter(deps.localDatasetsService, {
          parentFilterName: 'state',
          showInFilterComponent: false,
        }),
        new DashboardSubMarketSingleSelectFilter(deps.localDatasetsService, {}),
      ];
    },
  },
};
